/** Hashing, domain separation, Merkle roots and the anti-spam PoW check. */

import { sha256 } from '@noble/hashes/sha2';
import { concat, toHex, utf8, type Hex } from './bytes.ts';

export function h256(...parts: Uint8Array[]): Uint8Array {
  return sha256(concat(...parts));
}

/** Domain-separated hash. Never hash two different things the same way. */
export function tag(domain: string, ...parts: Uint8Array[]): Uint8Array {
  const d = utf8(domain);
  const len = new Uint8Array([d.length]);
  return sha256(concat(len, d, ...parts));
}

export function tagHex(domain: string, ...parts: Uint8Array[]): Hex {
  return toHex(tag(domain, ...parts));
}

/** Binary Merkle root; odd nodes are promoted, not duplicated. */
export function merkleRoot(leaves: Uint8Array[]): Uint8Array {
  if (leaves.length === 0) return tag('face/merkle/empty');
  let level = leaves.map((l) => tag('face/merkle/leaf', l));
  while (level.length > 1) {
    const next: Uint8Array[] = [];
    for (let i = 0; i < level.length; i += 2) {
      next.push(i + 1 < level.length ? tag('face/merkle/node', level[i], level[i + 1]) : level[i]);
    }
    level = next;
  }
  return level[0];
}

/** Leading zero bits of a digest - the anti-spam work metric. */
export function leadingZeroBits(b: Uint8Array): number {
  let n = 0;
  for (let i = 0; i < b.length; i++) {
    if (b[i] === 0) {
      n += 8;
      continue;
    }
    let v = b[i];
    while ((v & 0x80) === 0) {
      n++;
      v = (v << 1) & 0xff;
    }
    break;
  }
  return n;
}

/**
 * FACE charges no transaction fees - a network for people without bank
 * accounts cannot also demand a balance before the first payment works.
 * Spam is priced in CPU instead: every vertex carries a small proof of
 * work, and the difficulty rises for senders who flood.
 */
export function powDigest(preimage: Uint8Array, powNonce: string): Uint8Array {
  return tag('face/pow/v1', preimage, utf8(powNonce));
}

export function powBits(preimage: Uint8Array, powNonce: string): number {
  return leadingZeroBits(powDigest(preimage, powNonce));
}

export function checkPow(preimage: Uint8Array, powNonce: string, required: number): boolean {
  return powBits(preimage, powNonce) >= required;
}

/** Grind a PoW nonce. Yields to the event loop so a browser tab stays alive. */
export async function solvePow(
  preimage: Uint8Array,
  required: number,
  onProgress?: (tries: number) => void,
): Promise<string> {
  let i = 0;
  for (;;) {
    for (let batch = 0; batch < 4096; batch++, i++) {
      const nonce = i.toString(36);
      if (leadingZeroBits(powDigest(preimage, nonce)) >= required) return nonce;
    }
    onProgress?.(i);
    await new Promise((r) => setTimeout(r, 0));
  }
}
